import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [retryCount, setRetryCount] = useState(0);
  const [isAdditionalCampaign, setIsAdditionalCampaign] = useState(false);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, retryCount]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setIsAdditionalCampaign(response.data.is_additional_campaign || false);
        
        // Push subscribe event to dataLayer when payment is confirmed
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'subscribe'
        });
        
        toast.success('Zahlung erfolgreich!');
      } else if (retryCount < 5) {
        setTimeout(() => setRetryCount(retryCount + 1), 2000);
      } else {
        setStatus('pending');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (retryCount < 5) {
        setTimeout(() => setRetryCount(retryCount + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  const handleContinue = () => {
    if (isAdditionalCampaign) {
      // For additional campaigns, skip survey and go directly to dashboard
      navigate('/dashboard');
    } else {
      // For first campaign, show the survey
      navigate(`/post-payment-survey?session_id=${sessionId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-gray-100 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Zahlung wird überprüft...</h2>
            <p className="text-gray-600">Bitte warten Sie einen Moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" data-testid="payment-success-icon" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Zahlung erfolgreich!</h2>
            <p className="text-gray-600 mb-8">
              {isAdditionalCampaign 
                ? 'Ihre neue Kampagne wird jetzt von unserem Team geprüft und aktiviert. Sie erhalten eine E-Mail, sobald Ihre Kampagne live ist.'
                : 'Ihre Kampagne wird jetzt von unserem Team geprüft und aktiviert. Sie erhalten eine E-Mail, sobald Ihre Kampagne live ist.'
              }
            </p>
            <Button
              onClick={handleContinue}
              className="w-full bg-blue-600 hover:bg-blue-700 py-6"
              data-testid="go-to-dashboard-btn"
            >
              {isAdditionalCampaign ? 'Zum Dashboard' : 'Weiter'}
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⏳</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Zahlung wird verarbeitet</h2>
            <p className="text-gray-600 mb-8">
              Die Zahlung wird noch verarbeitet. Bitte überprüfen Sie Ihr Dashboard in einigen Minuten.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-blue-600 hover:bg-blue-700 py-6"
            >
              Zum Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Fehler</h2>
            <p className="text-gray-600 mb-8">
              Es gab einen Fehler bei der Überprüfung der Zahlung. Bitte kontaktieren Sie den Support.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full py-6"
            >
              Zum Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;